import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Step 1: "You're almost ready" stage summary (US2, T042). Shows the progress
 * already made (Account ✓, Business information ✓) with "Learn BizPlus POS"
 * as the current stage — the owner is never restarted at 0%.
 */

export interface SetupProgressStepProps {
  /** Business name; falls back to an owner-friendly greeting when absent. */
  businessName?: string;
  ownerName?: string;
}

type StageState = 'complete' | 'current';

const STAGES: Array<{ label: string; state: StageState }> = [
  { label: 'Account', state: 'complete' },
  { label: 'Business information', state: 'complete' },
  { label: 'Learn BizPlus POS', state: 'current' },
];

const SetupProgressStep: React.FC<SetupProgressStepProps> = ({
  businessName,
  ownerName,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const trimmedBusiness = String(businessName || '').trim();
  const trimmedOwner = String(ownerName || '').trim();

  return (
    <View>
      <Text style={styles.greeting} testID="setup-progress-greeting">
        {trimmedBusiness ? (
          <>
            <Text style={styles.greetingEmphasis}>{trimmedBusiness}</Text>
            {' is set up and ready to go.'}
          </>
        ) : trimmedOwner ? (
          <>
            {'Welcome, '}
            <Text style={styles.greetingEmphasis}>{trimmedOwner}</Text>
            {'. Your business is set up and ready to go.'}
          </>
        ) : (
          'Welcome. Your business is set up and ready to go.'
        )}
      </Text>

      <View style={styles.stageList} accessibilityRole="list">
        {STAGES.map(stage => {
          const complete = stage.state === 'complete';
          return (
            <View
              key={stage.label}
              style={styles.stageRow}
              accessible
              accessibilityLabel={
                complete
                  ? `${stage.label}, completed`
                  : `${stage.label}, current stage`
              }>
              <View
                style={[
                  styles.stageIcon,
                  complete ? styles.stageIconComplete : styles.stageIconCurrent,
                ]}>
                <Ionicons
                  name={complete ? 'checkmark' : 'ellipse'}
                  size={iconSize(complete ? 14 : 10)}
                  color={
                    complete ? theme.colors.surface : theme.colors.primary
                  }
                />
              </View>
              <Text
                style={[
                  styles.stageLabel,
                  !complete && styles.stageLabelCurrent,
                ]}>
                {stage.label}
              </Text>
              {complete ? (
                <Text style={styles.stageDone}>Done</Text>
              ) : (
                <Text style={styles.stageCurrent}>Up next</Text>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    greeting: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(22),
      color: theme.colors.text,
      marginBottom: v(20),
    },
    greetingEmphasis: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    stageList: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(16),
      gap: v(14),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderSecondary,
    },
    stageRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
    },
    stageIcon: {
      width: iconSize(24),
      height: iconSize(24),
      borderRadius: iconSize(12),
      alignItems: 'center',
      justifyContent: 'center',
    },
    stageIconComplete: {
      backgroundColor: theme.colors.success,
    },
    stageIconCurrent: {
      backgroundColor: withOpacity(theme.colors.primary, 0.14),
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.primary,
    },
    stageLabel: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    stageLabelCurrent: {
      fontFamily: GTFonts.semibold,
    },
    stageDone: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.successEmerald,
    },
    stageCurrent: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.primary,
    },
  });

export default SetupProgressStep;
